import React, { useState, useEffect } from 'react';
import { 
  subscribeToPush, 
  unsubscribeFromPush, 
  isPushSupported, 
  getNotificationPermission,
  testPushNotification,
  loadPushSettings,
  savePushSettings,
  updateNotificationSettings
} from '@/utils/pushNotifications';

interface PushNotificationPromptProps {
  onDismiss: () => void;
}

const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    // Check if push notifications are supported
    const supported = isPushSupported();
    setIsSupported(supported);
    
    if (supported) {
      const currentPermission = getNotificationPermission();
      setPermission(currentPermission);
      
      // Check if user has already been prompted
      const hasBeenPrompted = localStorage.getItem('riddl-push-prompted');
      
      if (!hasBeenPrompted && currentPermission === 'default') {
        // Show prompt after a short delay (soft opt-in)
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleSubscribe = async () => {
    setIsRequesting(true);
    
    try {
      const subscription = await subscribeToPush();
      
      if (subscription) {
        setIsSubscribed(true);
        setPermission('granted');
        
        // Save default settings
        const settings = loadPushSettings();
        settings.enabled = true;
        savePushSettings(settings);
        
        // Update settings on server
        await updateNotificationSettings(settings);
        
        // Mark as prompted
        localStorage.setItem('riddl-push-prompted', 'subscribed');
        
        setIsVisible(false);
        onDismiss();
      }
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsRequesting(true);
    
    try {
      const success = await unsubscribeFromPush();
      
      if (success) {
        setIsSubscribed(false);
        
        // Update settings
        const settings = loadPushSettings();
        settings.enabled = false;
        savePushSettings(settings);
        
        localStorage.setItem('riddl-push-prompted', 'unsubscribed');
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleTest = async () => {
    try {
      await testPushNotification();
    } catch (error) {
      console.error('Failed to send test notification:', error);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('riddl-push-prompted', 'dismissed');
    setIsVisible(false);
    onDismiss();
  };

  if (!isSupported) {
    return null;
  }

  if (!isVisible && permission !== 'granted') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-teal-200 dark:border-gray-700">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-teal-100 dark:bg-teal-900 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.5 19.5a2.5 2.5 0 01-2.5-2.5V7a2.5 2.5 0 012.5-2.5h15a2.5 2.5 0 012.5 2.5v10a2.5 2.5 0 01-2.5 2.5h-15z" />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Stay Updated with Riddl! 🔔
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
            Get notified when new riddles are available and never miss your daily challenge. 
            We'll send you gentle reminders to keep your streak going!
          </p>

          {/* Benefits */}
          <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-teal-800 dark:text-teal-200 mb-2">What you'll get:</h3>
            <ul className="text-sm text-teal-700 dark:text-teal-300 space-y-1">
              <li>• Daily riddle reminders</li>
              <li>• Gentle notifications (max 3 per day)</li>
              <li>• Customizable timing</li>
              <li>• Easy to unsubscribe anytime</li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            {permission === 'granted' && isSubscribed ? (
              <>
                <button
                  onClick={handleTest}
                  className="w-full py-3 px-6 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors duration-200 shadow-lg"
                >
                  Test Notification
                </button>
                <button
                  onClick={handleUnsubscribe}
                  disabled={isRequesting}
                  className="w-full py-3 px-6 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  {isRequesting ? 'Unsubscribing...' : 'Unsubscribe'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSubscribe}
                  disabled={isRequesting}
                  className="w-full py-3 px-6 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRequesting ? 'Subscribing...' : 'Enable Push Notifications'}
                </button>
                <button
                  onClick={handleDismiss}
                  className="w-full py-3 px-6 bg-transparent text-gray-600 dark:text-gray-400 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Maybe Later
                </button>
              </>
            )}
          </div>

          {/* Privacy note */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
            We respect your privacy. You can unsubscribe anytime in settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
